import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  GridLegacy as Grid,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import TallyBrownLogo from "../assets/tally-brown.png";

const Overview: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: theme.palette.background.default,
        color: theme.palette.primary.main,
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        <Box textAlign="center" mb={6}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 3,
              mb: 3,
            }}
          >
            <Box
              component="img"
              src={TallyBrownLogo}
              alt="Tally Logo"
              sx={{
                height: { xs: "50px", sm: "60px", md: "70px" },
              }}
            />
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4rem" },
                fontWeight: "bold",
                color: theme.palette.primary.main,
                margin: 0,
              }}
            >
              Tally
            </Typography>
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: "1.2rem", sm: "1.5rem" },
              mb: 6,
              color: theme.palette.primary.main,
              maxWidth: "700px",
              margin: "0 auto",
            }}
          >
            Powering the future of financial services with AI
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/")}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: "#000",
              fontSize: "1.1rem",
              px: 4,
              py: 1.5,
              mt: 3,
              "&:hover": {
                backgroundColor: theme.palette.primary.main,
                opacity: 0.9,
              },
            }}
          >
            Experience AI Finance
          </Button>
        </Box>

        <Grid container spacing={4} mb={6}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                backgroundColor: "transparent",
                border: `2px solid ${theme.palette.primary.main}`,
                height: "100%",
              }}
            >
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  color: theme.palette.primary.main,
                  mb: 2,
                }}
              >
                The AI Revolution in Finance
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.primary.main,
                  lineHeight: 1.7,
                }}
              >
                Tally is disrupting conventional financial reporting with
                cutting-edge AI technology. We're redefining how businesses
                access financial insights, transforming complex data analysis
                into simple conversations. Experience the future of finance
                where AI instantly delivers reports, predictions, and
                visualizations from your data.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                backgroundColor: "transparent",
                border: `2px solid ${theme.palette.primary.main}`,
                height: "100%",
              }}
            >
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  color: theme.palette.primary.main,
                  mb: 2,
                }}
              >
                Revolutionary Simplicity
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.primary.main,
                  lineHeight: 1.7,
                }}
              >
                Forget complex dashboards and steep learning curves. With Tally,
                you simply ask questions in natural language and receive
                intelligent responses instantly. Our AI technology transforms
                the way you interact with financial data, making advanced
                analytics accessible to everyone.
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Box mb={6}>
          <Typography
            variant="h3"
            textAlign="center"
            gutterBottom
            sx={{
              color: theme.palette.primary.main,
              mb: 4,
            }}
          >
            AI Intelligence in Action
          </Typography>

          {/* Application Screenshot */}
          <Box
            sx={{
              textAlign: "center",
              mb: 6,
            }}
          >
            <Box
              component="img"
              src="/revenue_question_graph.png"
              alt="Tally AI generating financial reports and graphs"
              sx={{
                maxWidth: "100%",
                height: "auto",
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(169, 125, 88, 0.3)",
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.primary.main,
                mt: 2,
                fontStyle: "italic",
                opacity: 0.8,
              }}
            >
              Real-time AI analysis generating insights and visualizations from
              your data
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {[
              "What was my expenditure on account 3665 in June 2024?",
              "List the 3 largest expenses in March 2020 on account 1000",
              "What was my total turnover in October 2022?",
              "Based on the profit for the last 6 months, what will our profit be next month?",
              "Draw me a graph of total costs per month for 2023",
              "According to the budget I uploaded, am I on track to meet my goals?",
            ].map((question, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    backgroundColor: "transparent",
                    border: `1px solid ${theme.palette.primary.main}`,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.primary.main,
                      fontStyle: "italic",
                    }}
                  >
                    "{question}"
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box mb={6}>
          <Typography
            variant="h3"
            textAlign="center"
            gutterBottom
            sx={{
              color: theme.palette.primary.main,
              mb: 4,
            }}
          >
            Seamless AI-Powered Connections
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 4,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 3,
                backgroundColor: "transparent",
                border: `2px solid ${theme.palette.primary.main}`,
                textAlign: "center",
                flex: "1",
                maxWidth: "400px",
                minWidth: "300px",
              }}
            >
              <Typography
                variant="h5"
                gutterBottom
                sx={{ color: theme.palette.primary.main }}
              >
                e-conomic
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.primary.main }}
              >
                Revolutionary AI integration transforms your accounting data
                into intelligent insights
              </Typography>
            </Paper>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                backgroundColor: "transparent",
                border: `2px solid ${theme.palette.primary.main}`,
                textAlign: "center",
                flex: "1",
                maxWidth: "400px",
                minWidth: "300px",
              }}
            >
              <Typography
                variant="h5"
                gutterBottom
                sx={{ color: theme.palette.primary.main }}
              >
                Google Sheets
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.primary.main }}
              >
                AI-powered analysis turns your spreadsheets into conversational
                financial intelligence
              </Typography>
            </Paper>
          </Box>
        </Box>

        <Box textAlign="center" mb={4}>
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              color: theme.palette.primary.main,
              mb: 3,
            }}
          >
            Join the Financial Revolution
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.primary.main,
              mb: 3,
              maxWidth: "650px",
              margin: "0 auto",
            }}
          >
            Be part of the future of financial services. Experience how AI is
            transforming the way businesses understand their financial data.
            Start your journey with cutting-edge technology that's reshaping
            finance.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/")}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: "#000",
              fontSize: "1.1rem",
              px: 5,
              py: 1.5,
              mt: 4,
              fontWeight: "bold",
              "&:hover": {
                backgroundColor: theme.palette.primary.main,
                opacity: 0.9,
              },
            }}
          >
            Experience the Future
          </Button>
        </Box>

        {/* Privacy Policy Footer */}
        <Box
          textAlign="center"
          sx={{
            py: 2,
            borderTop: `1px solid ${theme.palette.primary.main}`,
            opacity: 0.7,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.primary.main,
              fontSize: "0.9rem",
            }}
          >
            By using this service, you acknowledge you've read our{" "}
            <a
              href="/policies/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: theme.palette.primary.main,
                textDecoration: "underline",
              }}
            >
              Privacy Policy
            </a>
            .
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Overview;
